import { CounterpartyDisabledFilter } from "@common/constants/counterparty";
import { BillStatus } from "@common/types/bill";
import {
    CalendarDateFilters,
    FiltersTypes,
} from "@common/types/filters/general";
import { InvoiceStatus } from "@common/types/invoice";
import { ClassFilterType } from "@common/types/class";
import { CatalogItemFilterType } from "@common/types/catalogItem";
import {
    RuleActionType,
    RuleSourceFilter,
    RuleStatus,
} from "@common/types/transactionRule";
import { formatISO, parseISO } from "date-fns";

export type FiltersChangeFn = (payload: Partial<FiltersTypes>) => void;

export const defaultInvoiceStatuses: InvoiceStatus[] = [
    InvoiceStatus.DRAFT,
    InvoiceStatus.OPEN,
    InvoiceStatus.PAID,
];

export const defaultBillStatuses: BillStatus[] = [
    BillStatus.DRAFT,
    BillStatus.OPEN,
    BillStatus.PAID,
];

export const defaultRuleStatuses: RuleStatus[] = [
    RuleStatus.ENABLED,
    RuleStatus.ARCHIVED,
];

export const defaultRuleSources: RuleSourceFilter[] = [
    RuleSourceFilter.CUSTOM,
    RuleSourceFilter.MATCHED_KICK,
];

export const ruleSourceDisplayLabels: Record<RuleSourceFilter, string> = {
    [RuleSourceFilter.CUSTOM]: "Custom rules",
    [RuleSourceFilter.MATCHED_KICK]: "Matched Kick rules",
    [RuleSourceFilter.UNMATCHED_KICK]: "Unmatched Kick rules",
};

export const ruleActionDisplayLabels: Record<RuleActionType, string> = {
    [RuleActionType.CATEGORY]: "Update category",
    [RuleActionType.ENTITY]: "Move to entity",
    [RuleActionType.ACCOUNT_ID]: "Assign to cash ledger",
    [RuleActionType.ACCOUNT_NAME]: "Assign to cash ledger",
    [RuleActionType.ACCRUAL_ACCOUNT_ID]: "Assign to accrual ledger",
    [RuleActionType.ACCRUAL_ACCOUNT_NAME]: "Assign to accrual ledger",
    [RuleActionType.MEMO]: "Add memo",
    [RuleActionType.COUNTERPARTY]: "Assign counterparty",
    [RuleActionType.CUSTOMER]: "Assign customer",
    [RuleActionType.CLASS]: "Set class",
    [RuleActionType.SPLIT]: "Split",
    [RuleActionType.TRANSFER_MATCHING]: "Disallow matching",
    [RuleActionType.ASSIGN_TO_SCHEDULE]: "Set recognition schedule",
};

export enum FilterTabs {
    CATEGORY = "category",
    DATE = "date",
    AMOUNT = "amount",
    ACCOUNT = "account",
    RULES = "rules",
    SIMILAR_TO = "similarTo",
    CLASS = "class",
    NOTE = "note",
    RECEIPTS = "receipts",
    MATCHED = "matched",
    SPLIT = "split",
    RECONCILIATION = "reconciliation",
    CASH_ITEM_TYPE = "cashItemType",
    COUNTERPARTY = "counterparty",
    COUNTERPARTY_CREATED_BY = "counterpartyCreatedBy",
    CUSTOMER = "customer",
    DOCUMENT_STATUS = "documentStatus",
    DOCUMENT_CATEGORY = "documentCategory",
    ACCOUNT_CODE = "accountCode",
    ACCOUNT_STATUS = "accountStatus",
    ACCOUNT_SOURCE = "accountSource",
    ACCOUNT_ROLE_PRESENCE = "accountRolePresence",
    ACCOUNT_RULE_PRESENCE = "accountRulePresence",
    FORM_1099_BOX = "form1099Box",
    COA_TEMPLATE_GROUPS = "coaTemplateGroups",
    JOURNAL_ENTRY_SOURCES = "journalEntrySources",
    INVOICE_STATUSES = "invoiceStatuses",
    EXTERNAL_INVOICE_STATUSES = "externalInvoiceStatuses",
    BILLABLE_EXPENSE_INVOICE_STATUSES = "billableExpenseInvoiceStatuses",
    CATALOG_ITEM = "catalogItem",
    INVOICE_SOURCE_TEMPLATE = "invoiceSourceTemplate",
    CATALOG_ITEM_INCOME_ACCOUNT = "catalogItemIncomeAccount",
    CATALOG_ITEM_RECEIVABLES_ACCOUNT = "catalogItemReceivablesAccount",
    SUBSCRIPTION = "subscription",
    BILL_STATUSES = "billStatuses",
    EXTERNAL_BILL_STATUSES = "externalBillStatuses",
    OVERDUE = "overdue",
    DUE = "due",
    RULES_STATUS = "rulesStatus",
    RULES_SOURCE = "rulesSource",
    RULES_ACTION = "rulesAction",
    LEDGER = "ledger",
    CASH_GENERAL_LEDGER = "cashGeneralLedger",
    ACCRUAL_GENERAL_LEDGER = "accrualGeneralLedger",
    ACCRUAL_LEDGER = "accrualLedger",
    TRANSACTION_IDS = "transactionIds",
    BILL_IDS = "billIds",
    INVOICE_IDS = "invoiceIds",
    TRANSACTION_LOCKED = "transactionLocked",
    STATUS = "status",
    MATCH_STATUS = "matchStatus",

    // Clients Page filters
    PLANS = "plans",
    WHO_PAYS = "whoPays",
    SERVICES = "services",
    TEAM_MEMBERS = "teamMembers",
    ASSIGNEE = "assignee",
    LEGAL_TYPE = "legalType",
    ENTITY_INDUSTRY = "entityIndustry",
    ENTITY_BASIS = "entityBasis",
    START_DATE = "startDate",
    CLOSE_DATE = "closeDate",
    DUE_DATE = "dueDate",
    CLOSE_STAGE = "closeStage",
    PRIORITY = "priority",
    UNREAD = "unread",

    UPLOADER = "uploader",

    // Documents Page filters
    DOCUMENT_PERIOD = "documentPeriod",
    UPLOAD_DATE = "uploadDate",
    DOCUMENT_TYPE = "documentType",

    AUDIT_LOG = "auditLog",

    // Template-specific simple filter toggles (task template filter overlay)
    TEMPLATE_COUNTERPARTY = "templateCounterparty",
    TEMPLATE_CUSTOMER = "templateCustomer",
    TEMPLATE_CLASS = "templateClass",
    TEMPLATE_BASIS = "templateBasis",
    TEMPLATE_LAST_EDITED_BY = "templateLastEditedBy",
    TEMPLATE_OWNER = "templateOwner",
    TEMPLATE_CREATED_BY = "templateCreatedBy",
    TEMPLATE_TYPE = "templateType",
    TEMPLATE_IDS = "templateIds",

    // Tasks Page filters
    TASK_ASSIGNEE = "taskAssignee",
    TASK_CREATED_BY = "taskCreatedBy",
    TASK_CLIENT = "taskClient",
    TASK_CREATED_ON = "taskCreatedOn",
    TASK_COMPLETED_BY = "taskCompletedBy",
    TASK_COMPLETED_ON = "taskCompletedOn",
    TASK_CLOSE = "taskClose",
    TASK_PRIORITY = "taskPriority",
    TASK_ATTACHMENTS = "taskAttachments",
    TASK_GROUP = "taskGroup",
    TASK_DEPENDENCIES = "taskDependencies",
    TASK_CHAT = "taskChat",
    TASK_STAGE = "taskStage",

    // Org task templates page filters
    ORG_TASK_TEMPLATE_TYPE = "orgTaskTemplateType",
    ORG_TASK_TEMPLATE_GROUP = "orgTaskTemplateGroup",
    ORG_TASK_TEMPLATE_ASSIGNEE_ROLE = "orgTaskTemplateAssigneeRole",
    ORG_TASK_TEMPLATE_CREATED_BY = "orgTaskTemplateCreatedBy",

    // Work Page org-specific filters (UUID-keyed)
    ORG_CLOSE_STAGE = "orgCloseStage",
    CLOSE_STATUS = "closeStatuses",
    ORG_SERVICE = "orgService",
    CLOSE_TEMPLATE = "closeTemplate",
    APPLICATION_TYPE = "applicationTypes",
    PROGRESS = "progressStatuses",
    HEALTH = "healthStatuses",
    WORK_CLIENT = "workClient",

    // Work Page date filters
    WORK_START_DATE = "workStartDate",
    WORK_DELIVERY_DATE = "workDeliveryDate",

    // Service template schedule occurrences drawer date filter
    APPLICATION_DATE = "applicationDate",

    // CoA-template applied-entities drawer date filters
    COA_LAST_UPDATED = "coaLastUpdated",
    COA_APPLIED_AT = "coaAppliedAt",

    // Work Page numeric filters
    DAYS_IN_STAGE = "daysInStage",
    REVIEW = "review",
    RULES_REVIEW = "rulesReview",
    COUNTERPARTY_REVIEW = "counterpartyReview",
    COUNTERPARTY_DISABLED = "counterpartyDisabled",
    ENGAGEMENT_TEAM = "engagementTeam",
    ELIGIBLE_1099 = "eligible1099",
    EXCLUDE_1099 = "exclude1099",
    LEDGER_GROUP = "ledgerGroup",

    // Schedules Page filters
    SCHEDULE_PERIOD = "schedulePeriod",
    SCHEDULE_TEMPLATE = "scheduleTemplate",
    SCHEDULE_CLIENT = "scheduleClient",
    SCHEDULES_REPORT_TEMPLATE = "schedulesReportTemplate",
    SCHEDULES_REPORT_GROUP = "schedulesReportGroup",
    HOLDING_ACCOUNT = "holdingAccount",
    TERM_START = "termStart",
    TERM_END = "termEnd",

    INVOICE_TERMS = "invoiceTerms",
    INVOICE_SENT_STATUS = "invoiceSentStatus",
    INVOICE_CREATED_ON = "invoiceCreatedOn",
    INVOICE_DUE_DATE = "invoiceDueDate",
    INVOICE_DATE = "invoiceDate",

    // A/R Aging Detail: aging-bucket period filter, rendered inside the
    // standard filters overlay (replaces the old standalone Bucket button).
    AGING_PERIOD = "agingPeriod",

    // Invoice/bill drafts filters
    DRAFT_SOURCE = "draftSource",
    DRAFT_COUNTERPARTY = "draftCounterparty",
    DRAFT_DUE_DATE = "draftDueDate",
    DRAFT_STATUS = "draftStatus",
}

// Virtual filter tabs that don't render in the UI but can display active filter labels
export const VIRTUAL_FILTER_TABS: FilterTabs[] = [
    FilterTabs.TRANSACTION_IDS,
    FilterTabs.BILL_IDS,
    FilterTabs.INVOICE_IDS,
    FilterTabs.AUDIT_LOG,
    FilterTabs.TEMPLATE_IDS,
];

export type ActiveFilter =
    | "date"
    | "category"
    | "classes"
    | "direction"
    | "amount"
    | "rounding"
    | "rules"
    | "similarTo"
    | "matchTypes"
    | "isSplitted"
    | "isReconciled"
    | "transactionTypes"
    | "note"
    | "receipts"
    | "counterparties"
    | "counterpartyCreatedBy"
    | "customers"
    | "accounts"
    | "subaccounts"
    | "documentImported"
    | "documentReconciled"
    | "documentMatched"
    | "documentCategoryIds"
    | "accountCodes"
    | "accountStatus"
    | "accountSource"
    | "accountRolePresence"
    | "accountRulePresence"
    | "form1099Boxes"
    | "coaTemplateGroupKeys"
    | "cashGeneralLedgerRoles"
    | "accrualGeneralLedgerRoles"
    | "journalEntrySources"
    | "invoiceStatuses"
    | "externalInvoiceStatuses"
    | "billableExpenseInvoiceStatuses"
    | "catalogItems"
    | "invoiceSourceTemplates"
    | "incomeAccountIds"
    | "receivablesAccountIds"
    | "isSubscription"
    | "billStatuses"
    | "externalBillStatuses"
    | "overdue"
    | "due"
    | "rulesStatus"
    | "rulesSource"
    | "ruleActionTypes"
    | "coaAccounts"
    | "accrualCoaAccounts"
    | "transactionIds"
    | "billIds"
    | "invoiceIds"
    | "plans"
    | "whoPays"
    | "services"
    | "startDate"
    | "teamMembers"
    | "assignee"
    | "legalType"
    | "entityIndustries"
    | "entityBases"
    | "closeDate"
    | "dueDate"
    | "closeStage"
    | "priority"
    | "transactionLocked"
    | "statuses"
    | "matchStatuses"
    | "draftSources"
    | "draftCounterpartyStates"
    | "hasDueDate"
    | "accrualReviewStatuses"
    | "hasUnread"
    | "uploaderIds"
    | "documentPeriod"
    | "uploadDate"
    | "fileExtensions"
    | "auditLogFilter"
    | "templateCounterparty"
    | "templateCustomer"
    | "templateClass"
    | "templateBasis"
    | "templateLastEditedBy"
    | "templateOwner"
    | "templateCreatedBy"
    | "templateType"
    | "templateIds"
    | "taskAssignee"
    | "taskCreatedBy"
    | "taskClient"
    | "taskCreatedOn"
    | "taskCompletedBy"
    | "taskCompletedOn"
    | "taskClose"
    | "taskPriority"
    | "taskAttachments"
    | "taskGroup"
    | "taskBlockedBy"
    | "taskBlocking"
    | "taskRelated"
    | "taskChat"
    | "taskStage"
    | "orgTaskTemplateType"
    | "orgTaskTemplateGroup"
    | "orgTaskTemplateAssigneeRole"
    | "orgTaskTemplateCreatedBy"
    | "orgCloseStage"
    | "closeStatuses"
    | "orgService"
    | "closeTemplate"
    | "applicationTypes"
    | "progressStatuses"
    | "healthStatuses"
    | "workClient"
    | "workStartDate"
    | "workDeliveryDate"
    | "applicationDate"
    | "coaLastUpdated"
    | "coaAppliedAt"
    | "daysInStage"
    | "reviewedFields"
    | "unreviewedFields"
    | "reviewer"
    | "ruleReviewStatuses"
    | "ruleReviewer"
    | "counterpartyReviewedFields"
    | "counterpartyUnreviewedFields"
    | "counterpartyReviewer"
    | "disabledFilter"
    | "engagementTeam"
    | "schedulePeriod"
    | "scheduleTemplate"
    | "scheduleClient"
    | "schedulesReportTemplate"
    | "schedulesReportGroup"
    | "holdingAccount"
    | "termStart"
    | "termEnd"
    | "invoiceTerms"
    | "invoiceSentStatus"
    | "invoiceCreatedOn"
    | "invoiceDueDate"
    | "invoiceDate"
    | "agingPeriod"
    | "isEligible1099"
    | "excludeCreditCardTransactions"
    | "excludePaymentProcessorTransactions"
    | "includeExcludedAmounts"
    | "excludeFrom1099"
    | "accountClasses";

export const resetPayload: Record<
    ActiveFilter,
    Partial<FiltersTypes & CalendarDateFilters>
> = {
    category: { category: undefined },
    classes: {
        classes: undefined,
        classFilterType: ClassFilterType.ANY_SELECTED,
    },
    direction: { direction: undefined },
    amount: { minAmount: undefined, maxAmount: undefined },
    rounding: {
        amountRounded: undefined,
        amountRoundingLevel: undefined,
    },
    date: {
        start: undefined,
        end: undefined,
        startDate: undefined,
        endDate: undefined,
    },
    rules: { transactionRulesIds: undefined },
    similarTo: {
        normalizedDescriptionId: undefined,
        normalizedDescriptionIds: undefined,
    },
    matchTypes: { matchTypes: undefined },
    isSplitted: { isSplitted: undefined, isNotSplitted: undefined },
    isReconciled: { isReconciled: undefined, isNotReconciled: undefined },
    transactionTypes: { transactionTypes: undefined },
    note: {
        hasNote: undefined,
        noNote: undefined,
        requiresNote: undefined,
    },
    receipts: {
        hasReceipt: undefined,
        noReceipt: undefined,
        requiresReceipt: undefined,
    },
    counterparties: {
        counterpartiesIds: undefined,
        withEmptyOrUnverifiedCounterparty: undefined,
        hasCounterparty: undefined,
    },
    counterpartyCreatedBy: {
        createdByKick: undefined,
        createdByUserIds: undefined,
        createdBySources: undefined,
    },
    customers: {
        customerIds: undefined,
        withEmptyCustomer: undefined,
        hasCustomer: undefined,
    },
    accounts: {
        accountIds: undefined,
        hasNoFinancialAccount: undefined,
        invoiceSources: undefined,
    },
    subaccounts: { subaccountIds: undefined },
    documentImported: {
        isImported: undefined,
        isImporting: undefined,
        isNotImported: undefined,
    },
    documentReconciled: {
        isDocReconciled: undefined,
        isDocReconciling: undefined,
        isDocNotReconciled: undefined,
    },
    documentMatched: {
        isDocMatched: undefined,
        isDocNotMatched: undefined,
    },
    documentCategoryIds: {
        documentCategoryIds: undefined,
    },
    accountCodes: { accountCodes: undefined },
    accountStatus: { accountStatus: undefined },
    accountSource: { accountSource: undefined },
    accountRolePresence: { accountRolePresence: undefined },
    accountRulePresence: { accountRulePresence: undefined },
    form1099Boxes: { form1099Boxes: undefined },
    coaTemplateGroupKeys: { coaTemplateGroupKeys: undefined },
    schedulesReportTemplate: { scheduleTemplateIds: undefined },
    schedulesReportGroup: { scheduleGroups: undefined },
    holdingAccount: { holdingAccountIds: undefined },
    termStart: { termStartFrom: undefined, termStartTo: undefined },
    termEnd: { termEndFrom: undefined, termEndTo: undefined },
    invoiceTerms: { termTypes: undefined },
    invoiceSentStatus: { sentStatuses: undefined, sentByUserIds: undefined },
    invoiceCreatedOn: { createdOnFrom: undefined, createdOnTo: undefined },
    invoiceDueDate: { dueDateFrom: undefined, dueDateTo: undefined },
    invoiceDate: { invoiceDateFrom: undefined, invoiceDateTo: undefined },
    agingPeriod: { agingBuckets: undefined },
    cashGeneralLedgerRoles: {
        cashGeneralLedgerRoles: undefined,
        cashGeneralLedgerTypes: undefined,
    },
    accrualGeneralLedgerRoles: {
        accrualGeneralLedgerRoles: undefined,
        accrualGeneralLedgerTypes: undefined,
    },
    journalEntrySources: { journalEntrySources: undefined },
    invoiceStatuses: { invoiceStatuses: defaultInvoiceStatuses },
    externalInvoiceStatuses: {
        externalInvoiceStatuses: defaultInvoiceStatuses,
    },
    billableExpenseInvoiceStatuses: {
        billableExpenseInvoiceStatuses: undefined,
    },
    catalogItems: {
        catalogItemIds: undefined,
        catalogItemFilterType: CatalogItemFilterType.ANY_SELECTED,
    },
    invoiceSourceTemplates: { sourceTemplateIds: undefined },
    incomeAccountIds: { incomeAccountIds: undefined },
    receivablesAccountIds: { receivablesAccountIds: undefined },
    isSubscription: { isSubscription: undefined },
    billStatuses: { billStatuses: defaultBillStatuses },
    externalBillStatuses: { externalBillStatuses: defaultBillStatuses },
    overdue: {
        overdue: undefined,
        minOverdue: undefined,
        maxOverdue: undefined,
    },
    due: {
        overdue: undefined,
        minDue: undefined,
        maxDue: undefined,
    },
    rulesStatus: { ruleStatuses: defaultRuleStatuses },
    rulesSource: { ruleSources: defaultRuleSources },
    ruleActionTypes: { ruleActionTypes: undefined },
    coaAccounts: { coaAccounts: undefined, coaFilterType: undefined },
    accrualCoaAccounts: {
        accrualCoaAccounts: undefined,
        includeUnassignedAccrualAccount: undefined,
    },
    transactionIds: { transactionIds: undefined },
    billIds: { billIds: undefined },
    invoiceIds: { invoiceIds: undefined },
    plans: { plans: undefined },
    whoPays: { whoPays: undefined },
    services: { services: undefined },
    teamMembers: { teamMemberIds: undefined },
    assignee: { assigneeIds: undefined },
    legalType: { legalTypes: undefined },
    entityIndustries: { entityIndustries: undefined },
    entityBases: { entityBases: undefined },
    startDate: {
        bookkeepingStartDateStart: undefined,
        bookkeepingStartDateEnd: undefined,
    },
    closeDate: {
        closeDateStart: undefined,
        closeDateEnd: undefined,
    },
    dueDate: {
        dueDateStart: undefined,
        dueDateEnd: undefined,
    },
    closeStage: { closeStage: undefined },
    priority: { priority: undefined },
    transactionLocked: { transactionLocked: undefined },
    statuses: { statuses: undefined },
    matchStatuses: { matchStatuses: undefined },
    draftSources: { draftSources: undefined },
    draftCounterpartyStates: { draftCounterpartyStates: undefined },
    hasDueDate: { hasDueDate: undefined },
    accrualReviewStatuses: { accrualReviewStatuses: undefined },
    hasUnread: { hasUnread: undefined },
    uploaderIds: {
        uploaderIds: undefined,
        uploaderType: undefined,
        uploaderSources: undefined,
    },
    documentPeriod: { periodStart: undefined, periodEnd: undefined },
    uploadDate: { start: undefined, end: undefined },
    fileExtensions: { fileExtensions: undefined },
    auditLogFilter: { auditLogFilter: undefined },
    templateCounterparty: {
        counterpartiesIds: undefined,
        withEmptyOrUnverifiedCounterparty: undefined,
        hasCounterparty: undefined,
    },
    templateCustomer: {
        customerIds: undefined,
        withEmptyCustomer: undefined,
        hasCustomer: undefined,
    },
    templateClass: {
        classes: undefined,
        classFilterType: ClassFilterType.ANY_SELECTED,
    },
    templateBasis: { closeTemplateBases: undefined },
    templateLastEditedBy: { closeTemplateUpdatedByUserIds: undefined },
    templateOwner: { closeTemplateOwnerIds: undefined },
    templateCreatedBy: { invoiceTemplateCreatedByUserIds: undefined },
    templateType: { closeTemplateType: undefined },
    templateIds: { templateIds: undefined },
    taskAssignee: {
        taskAssigneeIds: undefined,
        isTaskUnassigned: undefined,
        isTaskAssignedToClient: undefined,
    },
    taskCreatedBy: {
        taskCreatedByIds: undefined,
        isTaskCreatedByClient: undefined,
    },
    taskClient: { taskClientIds: undefined },
    taskCreatedOn: {
        taskCreatedAtStart: undefined,
        taskCreatedAtEnd: undefined,
    },
    taskCompletedBy: { taskCompletedByIds: undefined },
    taskCompletedOn: {
        taskCompletedAtStart: undefined,
        taskCompletedAtEnd: undefined,
    },
    taskClose: { taskCloseIds: undefined },
    taskPriority: { taskPriorities: undefined },
    taskAttachments: { hasAttachments: undefined },
    taskGroup: { taskGroupIds: undefined },
    taskBlockedBy: { hasBlockedBy: undefined },
    taskBlocking: { hasBlocking: undefined },
    taskRelated: { hasRelated: undefined },
    taskChat: { hasUnreadChat: undefined },
    taskStage: { taskCloseStageIds: undefined },
    orgTaskTemplateType: { orgTaskTemplateTypes: undefined },
    orgTaskTemplateGroup: { orgTaskTemplateGroupIds: undefined },
    orgTaskTemplateAssigneeRole: { orgTaskTemplateAssigneeRoles: undefined },
    orgTaskTemplateCreatedBy: { orgTaskTemplateCreatedByUserIds: undefined },
    orgCloseStage: { closeStageIds: undefined },
    closeStatuses: { closeStatuses: undefined },
    orgService: { orgServiceIds: undefined, orgServiceNoTier: undefined },
    closeTemplate: { closeTemplateIds: undefined },
    applicationTypes: { applicationTypes: undefined },
    progressStatuses: { progressStatuses: undefined },
    healthStatuses: { healthStatuses: undefined },
    workClient: { workClientIds: undefined },
    workStartDate: {
        workStartDateStart: undefined,
        workStartDateEnd: undefined,
    },
    workDeliveryDate: {
        workDeliveryDateStart: undefined,
        workDeliveryDateEnd: undefined,
        workDeliveryDatePreset: undefined,
    },
    applicationDate: {
        applicationDateStart: undefined,
        applicationDateEnd: undefined,
    },
    coaLastUpdated: {
        coaLastUpdatedStart: undefined,
        coaLastUpdatedEnd: undefined,
    },
    coaAppliedAt: {
        coaAppliedAtStart: undefined,
        coaAppliedAtEnd: undefined,
    },
    daysInStage: {
        daysInStageOperator: undefined,
        daysInStageMin: undefined,
        daysInStageMax: undefined,
    },
    reviewedFields: {
        reviewedFields: undefined,
        reviewedFieldsMode: undefined,
    },
    unreviewedFields: {
        unreviewedFields: undefined,
        unreviewedFieldsMode: undefined,
    },
    reviewer: { reviewerIds: undefined, reviewerMode: undefined },
    ruleReviewStatuses: { ruleReviewStatuses: undefined },
    ruleReviewer: { ruleReviewerIds: undefined, ruleReviewerMode: undefined },
    counterpartyReviewedFields: {
        counterpartyReviewedFields: undefined,
        counterpartyReviewedFieldsMode: undefined,
    },
    counterpartyUnreviewedFields: {
        counterpartyUnreviewedFields: undefined,
        counterpartyUnreviewedFieldsMode: undefined,
    },
    counterpartyReviewer: {
        counterpartyReviewerIds: undefined,
        counterpartyReviewerMode: undefined,
    },
    disabledFilter: { disabledFilter: undefined },
    engagementTeam: {
        engagementTeamRoles: undefined,
        engagementTeamRoleOperator: undefined,
        engagementTeamMemberIds: undefined,
        engagementTeamMemberOperator: undefined,
    },
    isEligible1099: { isEligible1099: undefined },
    excludeCreditCardTransactions: {
        shouldExcludeCreditCardJournalEntries: undefined,
    },
    excludePaymentProcessorTransactions: {
        shouldExcludePaymentProcessorJournalEntries: undefined,
    },
    includeExcludedAmounts: {
        includeExcludedAmounts: undefined,
    },
    excludeFrom1099: {
        excludeFrom1099: undefined,
    },
    accountClasses: { accountClasses: undefined },
    schedulePeriod: { schedulePeriods: undefined },
    scheduleTemplate: { scheduleTemplateIds: undefined },
    scheduleClient: { scheduleClientIds: undefined },
};

type FilterValueMapFn<TIn, TOut> = (filters: TIn) => TOut;
interface FilterValueMapping<TMapped> {
    to: FilterValueMapFn<FiltersTypes, TMapped>;
    from: FilterValueMapFn<TMapped, FiltersTypes>;
}

function parseDateOnlyString(value: string | undefined): Date | undefined {
    return value ? parseISO(value) : undefined;
}

function formatDateOnlyString(value: Date | undefined): string | undefined {
    return value ? formatISO(value, { representation: "date" }) : undefined;
}

export const filterValueMapping = {
    [FilterTabs.START_DATE]: {
        to: ({ bookkeepingStartDateStart, bookkeepingStartDateEnd }) => ({
            start: bookkeepingStartDateStart,
            end: bookkeepingStartDateEnd,
        }),
        from: ({ start, end }) => ({
            bookkeepingStartDateStart: start,
            bookkeepingStartDateEnd: end,
        }),
    } as FilterValueMapping<{ start?: Date; end?: Date }>,
    [FilterTabs.TERM_START]: {
        to: ({ termStartFrom, termStartTo }) => ({
            start: parseDateOnlyString(termStartFrom),
            end: parseDateOnlyString(termStartTo),
        }),
        from: ({ start, end }) => ({
            termStartFrom: formatDateOnlyString(start),
            termStartTo: formatDateOnlyString(end),
        }),
    } as FilterValueMapping<{ start?: Date; end?: Date }>,
    [FilterTabs.TERM_END]: {
        to: ({ termEndFrom, termEndTo }) => ({
            start: parseDateOnlyString(termEndFrom),
            end: parseDateOnlyString(termEndTo),
        }),
        from: ({ start, end }) => ({
            termEndFrom: formatDateOnlyString(start),
            termEndTo: formatDateOnlyString(end),
        }),
    } as FilterValueMapping<{ start?: Date; end?: Date }>,
    [FilterTabs.INVOICE_CREATED_ON]: {
        to: ({ createdOnFrom, createdOnTo }) => ({
            start: parseDateOnlyString(createdOnFrom),
            end: parseDateOnlyString(createdOnTo),
        }),
        from: ({ start, end }: { start?: Date; end?: Date }) => ({
            createdOnFrom: formatDateOnlyString(start),
            createdOnTo: formatDateOnlyString(end),
        }),
    } satisfies FilterValueMapping<{ start?: Date; end?: Date }>,
    [FilterTabs.INVOICE_DUE_DATE]: {
        to: ({ dueDateFrom, dueDateTo }) => ({
            start: parseDateOnlyString(dueDateFrom),
            end: parseDateOnlyString(dueDateTo),
        }),
        from: ({ start, end }: { start?: Date; end?: Date }) => ({
            dueDateFrom: formatDateOnlyString(start),
            dueDateTo: formatDateOnlyString(end),
        }),
    } satisfies FilterValueMapping<{ start?: Date; end?: Date }>,
    [FilterTabs.INVOICE_DATE]: {
        to: ({ invoiceDateFrom, invoiceDateTo }) => ({
            start: parseDateOnlyString(invoiceDateFrom),
            end: parseDateOnlyString(invoiceDateTo),
        }),
        from: ({ start, end }: { start?: Date; end?: Date }) => ({
            invoiceDateFrom: formatDateOnlyString(start),
            invoiceDateTo: formatDateOnlyString(end),
        }),
    } satisfies FilterValueMapping<{ start?: Date; end?: Date }>,
    [FilterTabs.CLOSE_DATE]: {
        to: ({ closeDateStart, closeDateEnd }) => ({
            start: closeDateStart,
            end: closeDateEnd,
        }),
        from: ({ start, end }) => ({
            closeDateStart: start,
            closeDateEnd: end,
        }),
    } as FilterValueMapping<{ start?: Date; end?: Date }>,
    [FilterTabs.DUE_DATE]: {
        to: ({ dueDateStart, dueDateEnd }) => ({
            start: dueDateStart,
            end: dueDateEnd,
        }),
        from: ({ start, end }) => ({
            dueDateStart: start,
            dueDateEnd: end,
        }),
    } as FilterValueMapping<{ start?: Date; end?: Date }>,
    [FilterTabs.WORK_START_DATE]: {
        to: ({ workStartDateStart, workStartDateEnd }) => ({
            start: workStartDateStart,
            end: workStartDateEnd,
        }),
        from: ({ start, end }: { start?: Date; end?: Date }) => ({
            workStartDateStart: start,
            workStartDateEnd: end,
        }),
    } satisfies FilterValueMapping<{ start?: Date; end?: Date }>,
    [FilterTabs.WORK_DELIVERY_DATE]: {
        to: ({ workDeliveryDateStart, workDeliveryDateEnd }) => ({
            start: workDeliveryDateStart,
            end: workDeliveryDateEnd,
        }),
        from: ({ start, end }: { start?: Date; end?: Date }) => ({
            workDeliveryDateStart: start,
            workDeliveryDateEnd: end,
        }),
    } satisfies FilterValueMapping<{ start?: Date; end?: Date }>,
    [FilterTabs.APPLICATION_DATE]: {
        to: ({ applicationDateStart, applicationDateEnd }) => ({
            start: applicationDateStart,
            end: applicationDateEnd,
        }),
        from: ({ start, end }: { start?: Date; end?: Date }) => ({
            applicationDateStart: start,
            applicationDateEnd: end,
        }),
    } satisfies FilterValueMapping<{ start?: Date; end?: Date }>,
    [FilterTabs.COA_LAST_UPDATED]: {
        to: ({ coaLastUpdatedStart, coaLastUpdatedEnd }) => ({
            start: coaLastUpdatedStart,
            end: coaLastUpdatedEnd,
        }),
        from: ({ start, end }: { start?: Date; end?: Date }) => ({
            coaLastUpdatedStart: start,
            coaLastUpdatedEnd: end,
        }),
    } satisfies FilterValueMapping<{ start?: Date; end?: Date }>,
    [FilterTabs.COA_APPLIED_AT]: {
        to: ({ coaAppliedAtStart, coaAppliedAtEnd }) => ({
            start: coaAppliedAtStart,
            end: coaAppliedAtEnd,
        }),
        from: ({ start, end }: { start?: Date; end?: Date }) => ({
            coaAppliedAtStart: start,
            coaAppliedAtEnd: end,
        }),
    } satisfies FilterValueMapping<{ start?: Date; end?: Date }>,
    [FilterTabs.DOCUMENT_PERIOD]: {
        to: ({ periodStart, periodEnd }) => ({
            start: periodStart ? parseISO(periodStart) : undefined,
            end: periodEnd ? parseISO(periodEnd) : undefined,
        }),
        from: ({ start, end }) => ({
            periodStart: start
                ? formatISO(start, { representation: "date" })
                : undefined,
            periodEnd: end
                ? formatISO(end, { representation: "date" })
                : undefined,
        }),
    } as FilterValueMapping<{ start?: Date; end?: Date }>,
    [FilterTabs.TASK_CREATED_ON]: {
        to: ({ taskCreatedAtStart, taskCreatedAtEnd }) => ({
            start: taskCreatedAtStart,
            end: taskCreatedAtEnd,
        }),
        from: ({ start, end }: { start?: Date; end?: Date }) => ({
            taskCreatedAtStart: start,
            taskCreatedAtEnd: end,
        }),
    } satisfies FilterValueMapping<{ start?: Date; end?: Date }>,
    [FilterTabs.TASK_COMPLETED_ON]: {
        to: ({ taskCompletedAtStart, taskCompletedAtEnd }) => ({
            start: taskCompletedAtStart,
            end: taskCompletedAtEnd,
        }),
        from: ({ start, end }: { start?: Date; end?: Date }) => ({
            taskCompletedAtStart: start,
            taskCompletedAtEnd: end,
        }),
    } satisfies FilterValueMapping<{ start?: Date; end?: Date }>,
} as const;

export const activeFiltersOrder: ActiveFilter[] = [
    "date",
    "amount",
    "accounts",
    "subaccounts",
    "category",
    "classes",
    "counterparties",
    "counterpartyCreatedBy",
    "customers",
    "direction",
    "rounding",
    "rules",
    "similarTo",
    "note",
    "receipts",
    "matchTypes",
    "isSplitted",
    "isReconciled",
    "transactionTypes",
    "documentImported",
    "documentReconciled",
    "documentMatched",
    "documentCategoryIds",
    "accountCodes",
    "accountStatus",
    "accountSource",
    "accountRolePresence",
    "accountRulePresence",
    "form1099Boxes",
    "coaTemplateGroupKeys",
    "cashGeneralLedgerRoles",
    "accrualGeneralLedgerRoles",
    "journalEntrySources",
    "invoiceStatuses",
    "externalInvoiceStatuses",
    "billableExpenseInvoiceStatuses",
    "catalogItems",
    "invoiceSourceTemplates",
    "incomeAccountIds",
    "receivablesAccountIds",
    "isSubscription",
    "billStatuses",
    "externalBillStatuses",
    "overdue",
    "due",
    "rulesStatus",
    "rulesSource",
    "ruleActionTypes",
    "coaAccounts",
    "accrualCoaAccounts",
    "transactionIds",
    "billIds",
    "invoiceIds",
    "plans",
    "whoPays",
    "services",
    "startDate",
    "teamMembers",
    "assignee",
    "legalType",
    "entityIndustries",
    "entityBases",
    "closeStage",
    "orgCloseStage",
    "closeStatuses",
    "orgService",
    "closeTemplate",
    "applicationTypes",
    "healthStatuses",
    "priority",
    "transactionLocked",
    "statuses",
    "matchStatuses",
    "draftSources",
    "draftCounterpartyStates",
    "hasDueDate",
    "accrualReviewStatuses",
    "hasUnread",
    "closeDate",
    "dueDate",
    "uploaderIds",
    "documentPeriod",
    "uploadDate",
    "fileExtensions",
    "auditLogFilter",
    "templateCounterparty",
    "templateCustomer",
    "templateClass",
    "templateBasis",
    "templateLastEditedBy",
    "templateOwner",
    "templateCreatedBy",
    "templateType",
    "templateIds",
    "taskAssignee",
    "taskCreatedBy",
    "taskClient",
    "taskCreatedOn",
    "taskCompletedBy",
    "taskCompletedOn",
    "taskClose",
    "taskPriority",
    "taskAttachments",
    "taskGroup",
    "taskBlockedBy",
    "taskBlocking",
    "taskRelated",
    "taskChat",
    "taskStage",
    "orgTaskTemplateType",
    "orgTaskTemplateGroup",
    "orgTaskTemplateAssigneeRole",
    "orgTaskTemplateCreatedBy",
    "workStartDate",
    "workDeliveryDate",
    "applicationDate",
    "coaLastUpdated",
    "coaAppliedAt",
    "daysInStage",
    "reviewedFields",
    "unreviewedFields",
    "reviewer",
    "ruleReviewStatuses",
    "ruleReviewer",
    "counterpartyReviewedFields",
    "counterpartyUnreviewedFields",
    "counterpartyReviewer",
    "disabledFilter",
    "engagementTeam",
    "schedulePeriod",
    "scheduleTemplate",
    "scheduleClient",
    "schedulesReportTemplate",
    "schedulesReportGroup",
    "holdingAccount",
    "termStart",
    "termEnd",
    "invoiceTerms",
    "invoiceSentStatus",
    "invoiceCreatedOn",
    "invoiceDueDate",
    "invoiceDate",
    "agingPeriod",
    "isEligible1099",
    "excludeCreditCardTransactions",
    "excludePaymentProcessorTransactions",
    "includeExcludedAmounts",
    "excludeFrom1099",
    "accountClasses",
];

type PredicateFn = (filters: FiltersTypes & CalendarDateFilters) => boolean;

export const isActive: Record<ActiveFilter, PredicateFn> = {
    category: ({ category }) => !!category && category.length > 0,
    classes: ({ classes, classFilterType }) =>
        !!classes ||
        classFilterType === ClassFilterType.NO_CLASSES ||
        classFilterType === ClassFilterType.HAS_CLASSES,
    direction: ({ direction }) => !!direction,
    amount: ({ minAmount, maxAmount }) =>
        minAmount !== undefined || maxAmount !== undefined,
    rounding: ({ amountRounded }) => amountRounded !== undefined,
    date: ({ start, end, startDate, endDate }) =>
        start !== undefined ||
        end !== undefined ||
        startDate !== undefined ||
        endDate !== undefined,
    rules: ({ transactionRulesIds }) =>
        !!transactionRulesIds && transactionRulesIds.length > 0,
    similarTo: ({ normalizedDescriptionId, normalizedDescriptionIds }) =>
        !!normalizedDescriptionId ||
        (!!normalizedDescriptionIds && normalizedDescriptionIds.length > 0),
    matchTypes: ({ matchTypes }) => !!matchTypes && matchTypes.length > 0,
    isSplitted: ({ isSplitted, isNotSplitted }) =>
        Boolean(isSplitted) !== Boolean(isNotSplitted),
    isReconciled: ({ isReconciled, isNotReconciled }) =>
        Boolean(isReconciled) !== Boolean(isNotReconciled),
    transactionTypes: ({ transactionTypes }) => !!transactionTypes?.length,
    note: ({ hasNote, noNote, requiresNote }) =>
        [hasNote, noNote, requiresNote].some((value) => !!value),
    receipts: ({ hasReceipt, noReceipt, requiresReceipt }) =>
        [hasReceipt, noReceipt, requiresReceipt].some((value) => !!value),
    counterparties: ({
        counterpartiesIds,
        withEmptyOrUnverifiedCounterparty,
        hasCounterparty,
    }) =>
        (!!counterpartiesIds && counterpartiesIds.length > 0) ||
        Boolean(withEmptyOrUnverifiedCounterparty) ||
        Boolean(hasCounterparty),
    counterpartyCreatedBy: ({
        createdByKick,
        createdByUserIds,
        createdBySources,
    }) =>
        Boolean(createdByKick) ||
        (!!createdByUserIds && createdByUserIds.length > 0) ||
        (!!createdBySources && createdBySources.length > 0),
    customers: ({ customerIds, withEmptyCustomer, hasCustomer }) =>
        (!!customerIds && customerIds.length > 0) ||
        Boolean(withEmptyCustomer) ||
        Boolean(hasCustomer),
    accounts: ({ accountIds, hasNoFinancialAccount, invoiceSources }) =>
        (!!accountIds && accountIds.length > 0) ||
        Boolean(hasNoFinancialAccount) ||
        (!!invoiceSources && invoiceSources.length > 0),
    subaccounts: ({ subaccountIds }) =>
        !!subaccountIds && subaccountIds.length > 0,
    documentImported: ({ isImported, isImporting, isNotImported }) => {
        const selected = [isImported, isImporting, isNotImported].filter(
            Boolean,
        );
        return selected.length > 0 && selected.length < 3;
    },
    documentReconciled: ({
        isDocReconciled,
        isDocReconciling,
        isDocNotReconciled,
    }) => {
        const selected = [
            isDocReconciled,
            isDocReconciling,
            isDocNotReconciled,
        ].filter(Boolean);
        return selected.length > 0 && selected.length < 3;
    },
    documentMatched: ({ isDocMatched, isDocNotMatched }) =>
        Boolean(isDocMatched) !== Boolean(isDocNotMatched),
    documentCategoryIds: ({ documentCategoryIds }) =>
        !!documentCategoryIds && documentCategoryIds.length > 0,
    accountCodes: ({ accountCodes }) =>
        !!accountCodes && accountCodes.length > 0,
    accountStatus: ({ accountStatus }) => !!accountStatus,
    accountSource: ({ accountSource }) => !!accountSource,
    accountRolePresence: ({ accountRolePresence }) => !!accountRolePresence,
    accountRulePresence: ({ accountRulePresence }) => !!accountRulePresence,
    form1099Boxes: ({ form1099Boxes }) => !!form1099Boxes?.length,
    coaTemplateGroupKeys: ({ coaTemplateGroupKeys }) =>
        !!coaTemplateGroupKeys?.length,
    cashGeneralLedgerRoles: ({
        cashGeneralLedgerRoles,
        cashGeneralLedgerTypes,
    }) =>
        (!!cashGeneralLedgerRoles && cashGeneralLedgerRoles.length > 0) ||
        (!!cashGeneralLedgerTypes && cashGeneralLedgerTypes.length > 0),
    accrualGeneralLedgerRoles: ({
        accrualGeneralLedgerRoles,
        accrualGeneralLedgerTypes,
    }) =>
        (!!accrualGeneralLedgerRoles && accrualGeneralLedgerRoles.length > 0) ||
        (!!accrualGeneralLedgerTypes && accrualGeneralLedgerTypes.length > 0),
    journalEntrySources: ({ journalEntrySources }) =>
        !!journalEntrySources && journalEntrySources.length > 0,
    invoiceStatuses: ({ invoiceStatuses }) =>
        !!invoiceStatuses &&
        invoiceStatuses.length > 0 &&
        !isDefaultInvoiceStatuses(invoiceStatuses),
    externalInvoiceStatuses: ({ externalInvoiceStatuses }) =>
        !!externalInvoiceStatuses &&
        externalInvoiceStatuses.length > 0 &&
        !isDefaultInvoiceStatuses(externalInvoiceStatuses),
    billableExpenseInvoiceStatuses: ({ billableExpenseInvoiceStatuses }) =>
        !!billableExpenseInvoiceStatuses &&
        billableExpenseInvoiceStatuses.length > 0,
    catalogItems: ({ catalogItemIds }) =>
        !!catalogItemIds && catalogItemIds.length > 0,
    invoiceSourceTemplates: ({ sourceTemplateIds }) =>
        !!sourceTemplateIds && sourceTemplateIds.length > 0,
    isSubscription: ({ isSubscription }) => isSubscription !== undefined,
    billStatuses: ({ billStatuses }) =>
        !!billStatuses &&
        billStatuses.length > 0 &&
        !isDefaultBillStatuses(billStatuses),
    externalBillStatuses: ({ externalBillStatuses }) =>
        !!externalBillStatuses &&
        externalBillStatuses.length > 0 &&
        !isDefaultBillStatuses(externalBillStatuses),
    overdue: ({ overdue, minOverdue, maxOverdue }) =>
        overdue !== undefined ||
        minOverdue !== undefined ||
        maxOverdue !== undefined,
    due: ({ overdue, minDue, maxDue }) =>
        overdue !== undefined || minDue !== undefined || maxDue !== undefined,
    rulesStatus: ({ ruleStatuses }) =>
        !!ruleStatuses &&
        ruleStatuses.length > 0 &&
        !isDefaultRuleStatuses(ruleStatuses),
    rulesSource: ({ ruleSources }) =>
        !!ruleSources &&
        ruleSources.length > 0 &&
        !isDefaultRuleSources(ruleSources),
    ruleActionTypes: ({ ruleActionTypes }) =>
        !!ruleActionTypes && ruleActionTypes.length > 0,
    transactionIds: ({ transactionIds }) =>
        !!transactionIds && transactionIds.length > 0,
    billIds: ({ billIds }) => !!billIds && billIds.length > 0,
    invoiceIds: ({ invoiceIds }) => !!invoiceIds && invoiceIds.length > 0,
    coaAccounts: ({ coaAccounts }) => !!coaAccounts && coaAccounts.length > 0,
    accrualCoaAccounts: ({
        accrualCoaAccounts,
        includeUnassignedAccrualAccount,
    }) =>
        (!!accrualCoaAccounts && accrualCoaAccounts.length > 0) ||
        !!includeUnassignedAccrualAccount,
    incomeAccountIds: ({ incomeAccountIds }) =>
        !!incomeAccountIds && incomeAccountIds.length > 0,
    receivablesAccountIds: ({ receivablesAccountIds }) =>
        !!receivablesAccountIds && receivablesAccountIds.length > 0,
    plans: ({ plans }) => !!plans && plans.length > 0,
    whoPays: ({ whoPays }) => !!whoPays && whoPays.length > 0,
    services: ({ services }) => !!services && services.length > 0,
    teamMembers: ({ teamMemberIds }) =>
        !!teamMemberIds && teamMemberIds.length > 0,
    assignee: ({ assigneeIds }) => !!assigneeIds && assigneeIds.length > 0,
    legalType: ({ legalTypes }) => !!legalTypes && legalTypes.length > 0,
    entityIndustries: ({ entityIndustries }) =>
        !!entityIndustries && entityIndustries.length > 0,
    entityBases: ({ entityBases }) => !!entityBases && entityBases.length > 0,
    startDate: ({ bookkeepingStartDateStart, bookkeepingStartDateEnd }) =>
        !!bookkeepingStartDateStart || !!bookkeepingStartDateEnd,
    closeDate: ({ closeDateStart, closeDateEnd }) =>
        !!closeDateStart || !!closeDateEnd,
    dueDate: ({ dueDateStart, dueDateEnd }) => !!dueDateStart || !!dueDateEnd,
    closeStage: ({ closeStage }) => !!closeStage && closeStage.length > 0,
    priority: ({ priority }) => !!priority && priority.length > 0,
    transactionLocked: ({ transactionLocked }) => !!transactionLocked,
    statuses: ({ statuses }) => !!statuses && statuses.length > 0,
    matchStatuses: ({ matchStatuses }) =>
        !!matchStatuses && matchStatuses.length > 0,
    draftSources: ({ draftSources }) =>
        !!draftSources && draftSources.length > 0,
    draftCounterpartyStates: ({ draftCounterpartyStates }) =>
        !!draftCounterpartyStates && draftCounterpartyStates.length > 0,
    hasDueDate: ({ hasDueDate }) => typeof hasDueDate === "boolean",
    accrualReviewStatuses: ({ accrualReviewStatuses }) =>
        !!accrualReviewStatuses && accrualReviewStatuses.length > 0,
    hasUnread: ({ hasUnread }) => typeof hasUnread === "boolean",
    uploaderIds: ({ uploaderIds, uploaderType, uploaderSources }) =>
        (!!uploaderIds && uploaderIds.length > 0) ||
        !!uploaderType ||
        (!!uploaderSources && uploaderSources.length > 0),
    documentPeriod: ({ periodStart, periodEnd }) =>
        !!periodStart || !!periodEnd,
    uploadDate: ({ start, end }) => start !== undefined || end !== undefined,
    fileExtensions: ({ fileExtensions }) =>
        !!fileExtensions && fileExtensions.length > 0,
    auditLogFilter: ({ auditLogFilter }) => !!auditLogFilter,
    templateCounterparty: ({
        withEmptyOrUnverifiedCounterparty,
        hasCounterparty,
    }) =>
        Boolean(withEmptyOrUnverifiedCounterparty) || Boolean(hasCounterparty),
    templateCustomer: ({ withEmptyCustomer, hasCustomer }) =>
        Boolean(withEmptyCustomer) || Boolean(hasCustomer),
    templateClass: ({ classFilterType }) =>
        classFilterType === ClassFilterType.NO_CLASSES ||
        classFilterType === ClassFilterType.HAS_CLASSES,
    templateBasis: ({ closeTemplateBases }) =>
        !!closeTemplateBases && closeTemplateBases.length > 0,
    templateLastEditedBy: ({ closeTemplateUpdatedByUserIds }) =>
        !!closeTemplateUpdatedByUserIds &&
        closeTemplateUpdatedByUserIds.length > 0,
    templateOwner: ({ closeTemplateOwnerIds }) =>
        !!closeTemplateOwnerIds && closeTemplateOwnerIds.length > 0,
    templateCreatedBy: ({ invoiceTemplateCreatedByUserIds }) =>
        !!invoiceTemplateCreatedByUserIds &&
        invoiceTemplateCreatedByUserIds.length > 0,
    templateType: ({ closeTemplateType }) => !!closeTemplateType,
    templateIds: ({ templateIds }) => !!templateIds && templateIds.length > 0,
    taskAssignee: ({
        taskAssigneeIds,
        isTaskUnassigned,
        isTaskAssignedToClient,
    }) =>
        (!!taskAssigneeIds && taskAssigneeIds.length > 0) ||
        !!isTaskUnassigned ||
        !!isTaskAssignedToClient,
    taskCreatedBy: ({ taskCreatedByIds, isTaskCreatedByClient }) =>
        (!!taskCreatedByIds && taskCreatedByIds.length > 0) ||
        !!isTaskCreatedByClient,
    taskClient: ({ taskClientIds }) =>
        !!taskClientIds && taskClientIds.length > 0,
    taskCreatedOn: ({ taskCreatedAtStart, taskCreatedAtEnd }) =>
        !!taskCreatedAtStart || !!taskCreatedAtEnd,
    taskCompletedBy: ({ taskCompletedByIds }) =>
        !!taskCompletedByIds && taskCompletedByIds.length > 0,
    taskCompletedOn: ({ taskCompletedAtStart, taskCompletedAtEnd }) =>
        !!taskCompletedAtStart || !!taskCompletedAtEnd,
    taskClose: ({ taskCloseIds }) => !!taskCloseIds && taskCloseIds.length > 0,
    taskPriority: ({ taskPriorities }) =>
        !!taskPriorities && taskPriorities.length > 0,
    taskAttachments: ({ hasAttachments }) =>
        typeof hasAttachments === "boolean",
    taskGroup: ({ taskGroupIds }) => !!taskGroupIds && taskGroupIds.length > 0,
    taskBlockedBy: ({ hasBlockedBy }) => typeof hasBlockedBy === "boolean",
    taskBlocking: ({ hasBlocking }) => typeof hasBlocking === "boolean",
    taskRelated: ({ hasRelated }) => typeof hasRelated === "boolean",
    taskChat: ({ hasUnreadChat }) => typeof hasUnreadChat === "boolean",
    taskStage: ({ taskCloseStageIds }) =>
        !!taskCloseStageIds && taskCloseStageIds.length > 0,
    orgTaskTemplateType: ({ orgTaskTemplateTypes }) =>
        !!orgTaskTemplateTypes && orgTaskTemplateTypes.length > 0,
    orgTaskTemplateGroup: ({ orgTaskTemplateGroupIds }) =>
        !!orgTaskTemplateGroupIds && orgTaskTemplateGroupIds.length > 0,
    orgTaskTemplateAssigneeRole: ({ orgTaskTemplateAssigneeRoles }) =>
        !!orgTaskTemplateAssigneeRoles &&
        orgTaskTemplateAssigneeRoles.length > 0,
    orgTaskTemplateCreatedBy: ({ orgTaskTemplateCreatedByUserIds }) =>
        !!orgTaskTemplateCreatedByUserIds &&
        orgTaskTemplateCreatedByUserIds.length > 0,
    closeTemplate: ({ closeTemplateIds }) =>
        !!closeTemplateIds && closeTemplateIds.length > 0,
    orgCloseStage: ({ closeStageIds }) =>
        !!closeStageIds && closeStageIds.length > 0,
    closeStatuses: ({ closeStatuses }) =>
        !!closeStatuses && closeStatuses.length > 0,
    orgService: ({ orgServiceIds, orgServiceNoTier }) =>
        (!!orgServiceIds && orgServiceIds.length > 0) || !!orgServiceNoTier,
    applicationTypes: ({ applicationTypes }) =>
        !!applicationTypes && applicationTypes.length > 0,
    progressStatuses: ({ progressStatuses }) =>
        !!progressStatuses && progressStatuses.length > 0,
    healthStatuses: ({ healthStatuses }) =>
        !!healthStatuses && healthStatuses.length > 0,
    workClient: ({ workClientIds }) =>
        !!workClientIds && workClientIds.length > 0,
    workStartDate: ({ workStartDateStart, workStartDateEnd }) =>
        !!workStartDateStart || !!workStartDateEnd,
    workDeliveryDate: ({
        workDeliveryDateStart,
        workDeliveryDateEnd,
        workDeliveryDatePreset,
    }) =>
        !!workDeliveryDateStart ||
        !!workDeliveryDateEnd ||
        !!workDeliveryDatePreset,
    applicationDate: ({ applicationDateStart, applicationDateEnd }) =>
        !!applicationDateStart || !!applicationDateEnd,
    coaLastUpdated: ({ coaLastUpdatedStart, coaLastUpdatedEnd }) =>
        !!coaLastUpdatedStart || !!coaLastUpdatedEnd,
    coaAppliedAt: ({ coaAppliedAtStart, coaAppliedAtEnd }) =>
        !!coaAppliedAtStart || !!coaAppliedAtEnd,
    daysInStage: ({ daysInStageOperator, daysInStageMin, daysInStageMax }) => {
        if (!daysInStageOperator || daysInStageMin === undefined) {
            return false;
        }
        if (daysInStageOperator === "between") {
            return daysInStageMax !== undefined;
        }
        return true;
    },
    reviewedFields: ({ reviewedFields }) =>
        !!reviewedFields && reviewedFields.length > 0,
    unreviewedFields: ({ unreviewedFields }) =>
        !!unreviewedFields && unreviewedFields.length > 0,
    reviewer: ({ reviewerIds, reviewerMode }) =>
        (!!reviewerIds && reviewerIds.length > 0) || reviewerMode !== undefined,
    ruleReviewStatuses: ({ ruleReviewStatuses }) =>
        !!ruleReviewStatuses && ruleReviewStatuses.length > 0,
    ruleReviewer: ({ ruleReviewerIds }) =>
        !!ruleReviewerIds && ruleReviewerIds.length > 0,
    counterpartyReviewedFields: ({ counterpartyReviewedFields }) =>
        !!counterpartyReviewedFields && counterpartyReviewedFields.length > 0,
    counterpartyUnreviewedFields: ({ counterpartyUnreviewedFields }) =>
        !!counterpartyUnreviewedFields &&
        counterpartyUnreviewedFields.length > 0,
    counterpartyReviewer: ({
        counterpartyReviewerIds,
        counterpartyReviewerMode,
    }) =>
        (!!counterpartyReviewerIds && counterpartyReviewerIds.length > 0) ||
        counterpartyReviewerMode !== undefined,
    disabledFilter: ({ disabledFilter }) =>
        disabledFilter !== undefined &&
        disabledFilter !== CounterpartyDisabledFilter.ALL,
    engagementTeam: ({ engagementTeamRoles, engagementTeamMemberIds }) =>
        (!!engagementTeamRoles && engagementTeamRoles.length > 0) ||
        (!!engagementTeamMemberIds && engagementTeamMemberIds.length > 0),
    isEligible1099: ({ isEligible1099 }) => isEligible1099 !== undefined,
    excludeCreditCardTransactions: ({
        shouldExcludeCreditCardJournalEntries,
    }) => shouldExcludeCreditCardJournalEntries === true,
    excludePaymentProcessorTransactions: ({
        shouldExcludePaymentProcessorJournalEntries,
    }) => shouldExcludePaymentProcessorJournalEntries === true,
    includeExcludedAmounts: ({ includeExcludedAmounts }) =>
        includeExcludedAmounts === false,
    excludeFrom1099: ({ excludeFrom1099 }) => excludeFrom1099 !== undefined,
    accountClasses: ({ accountClasses }) =>
        !!accountClasses && accountClasses.length > 0,
    schedulePeriod: ({ schedulePeriods }) =>
        !!schedulePeriods && schedulePeriods.length > 0,
    scheduleTemplate: ({ scheduleTemplateIds }) =>
        !!scheduleTemplateIds && scheduleTemplateIds.length > 0,
    scheduleClient: ({ scheduleClientIds }) =>
        !!scheduleClientIds && scheduleClientIds.length > 0,
    schedulesReportTemplate: ({ scheduleTemplateIds }) =>
        !!scheduleTemplateIds && scheduleTemplateIds.length > 0,
    schedulesReportGroup: ({ scheduleGroups }) =>
        !!scheduleGroups && scheduleGroups.length > 0,
    holdingAccount: ({ holdingAccountIds }) =>
        !!holdingAccountIds && holdingAccountIds.length > 0,
    termStart: ({ termStartFrom, termStartTo }) =>
        !!termStartFrom || !!termStartTo,
    termEnd: ({ termEndFrom, termEndTo }) => !!termEndFrom || !!termEndTo,
    invoiceTerms: ({ termTypes }) => !!termTypes && termTypes.length > 0,
    invoiceSentStatus: ({ sentStatuses, sentByUserIds }) =>
        !!sentStatuses?.length || !!sentByUserIds?.length,
    invoiceCreatedOn: ({ createdOnFrom, createdOnTo }) =>
        !!createdOnFrom || !!createdOnTo,
    invoiceDueDate: ({ dueDateFrom, dueDateTo }) =>
        !!dueDateFrom || !!dueDateTo,
    invoiceDate: ({ invoiceDateFrom, invoiceDateTo }) =>
        !!invoiceDateFrom || !!invoiceDateTo,
    agingPeriod: ({ agingBuckets }) =>
        !!agingBuckets && agingBuckets.length > 0,
} as const;

export interface BaseFilterProps {
    filters: FiltersTypes;
    onChange: FiltersChangeFn;
}

export const ActiveFilterToFilterTabsMap: Record<ActiveFilter, FilterTabs> = {
    category: FilterTabs.CATEGORY,
    date: FilterTabs.DATE,
    amount: FilterTabs.AMOUNT,
    direction: FilterTabs.AMOUNT,
    rounding: FilterTabs.AMOUNT,
    accounts: FilterTabs.ACCOUNT,
    subaccounts: FilterTabs.ACCOUNT,
    rules: FilterTabs.RULES,
    similarTo: FilterTabs.SIMILAR_TO,
    classes: FilterTabs.CLASS,
    note: FilterTabs.NOTE,
    receipts: FilterTabs.RECEIPTS,
    matchTypes: FilterTabs.MATCHED,
    isSplitted: FilterTabs.SPLIT,
    isReconciled: FilterTabs.RECONCILIATION,
    transactionTypes: FilterTabs.CASH_ITEM_TYPE,
    counterparties: FilterTabs.COUNTERPARTY,
    counterpartyCreatedBy: FilterTabs.COUNTERPARTY_CREATED_BY,
    customers: FilterTabs.CUSTOMER,
    documentImported: FilterTabs.DOCUMENT_STATUS,
    documentReconciled: FilterTabs.DOCUMENT_STATUS,
    documentMatched: FilterTabs.DOCUMENT_STATUS,
    documentCategoryIds: FilterTabs.DOCUMENT_CATEGORY,
    accountCodes: FilterTabs.ACCOUNT_CODE,
    accountStatus: FilterTabs.ACCOUNT_STATUS,
    accountSource: FilterTabs.ACCOUNT_SOURCE,
    accountRolePresence: FilterTabs.ACCOUNT_ROLE_PRESENCE,
    accountRulePresence: FilterTabs.ACCOUNT_RULE_PRESENCE,
    form1099Boxes: FilterTabs.FORM_1099_BOX,
    coaTemplateGroupKeys: FilterTabs.COA_TEMPLATE_GROUPS,
    cashGeneralLedgerRoles: FilterTabs.CASH_GENERAL_LEDGER,
    accrualGeneralLedgerRoles: FilterTabs.ACCRUAL_GENERAL_LEDGER,
    journalEntrySources: FilterTabs.JOURNAL_ENTRY_SOURCES,
    invoiceStatuses: FilterTabs.INVOICE_STATUSES,
    externalInvoiceStatuses: FilterTabs.EXTERNAL_INVOICE_STATUSES,
    billableExpenseInvoiceStatuses:
        FilterTabs.BILLABLE_EXPENSE_INVOICE_STATUSES,
    catalogItems: FilterTabs.CATALOG_ITEM,
    invoiceSourceTemplates: FilterTabs.INVOICE_SOURCE_TEMPLATE,
    incomeAccountIds: FilterTabs.CATALOG_ITEM_INCOME_ACCOUNT,
    receivablesAccountIds: FilterTabs.CATALOG_ITEM_RECEIVABLES_ACCOUNT,
    isSubscription: FilterTabs.SUBSCRIPTION,
    billStatuses: FilterTabs.BILL_STATUSES,
    externalBillStatuses: FilterTabs.EXTERNAL_BILL_STATUSES,
    overdue: FilterTabs.OVERDUE,
    due: FilterTabs.DUE,
    rulesStatus: FilterTabs.RULES_STATUS,
    rulesSource: FilterTabs.RULES_SOURCE,
    ruleActionTypes: FilterTabs.RULES_ACTION,
    coaAccounts: FilterTabs.LEDGER,
    accrualCoaAccounts: FilterTabs.ACCRUAL_LEDGER,
    transactionIds: FilterTabs.TRANSACTION_IDS,
    billIds: FilterTabs.BILL_IDS,
    invoiceIds: FilterTabs.INVOICE_IDS,
    plans: FilterTabs.PLANS,
    whoPays: FilterTabs.WHO_PAYS,
    services: FilterTabs.SERVICES,
    teamMembers: FilterTabs.TEAM_MEMBERS,
    assignee: FilterTabs.ASSIGNEE,
    legalType: FilterTabs.LEGAL_TYPE,
    entityIndustries: FilterTabs.ENTITY_INDUSTRY,
    entityBases: FilterTabs.ENTITY_BASIS,
    startDate: FilterTabs.START_DATE,
    closeDate: FilterTabs.CLOSE_DATE,
    dueDate: FilterTabs.DUE_DATE,
    closeStage: FilterTabs.CLOSE_STAGE,
    priority: FilterTabs.PRIORITY,
    transactionLocked: FilterTabs.TRANSACTION_LOCKED,
    statuses: FilterTabs.STATUS,
    matchStatuses: FilterTabs.MATCH_STATUS,
    draftSources: FilterTabs.DRAFT_SOURCE,
    draftCounterpartyStates: FilterTabs.DRAFT_COUNTERPARTY,
    hasDueDate: FilterTabs.DRAFT_DUE_DATE,
    accrualReviewStatuses: FilterTabs.DRAFT_STATUS,
    hasUnread: FilterTabs.UNREAD,
    uploaderIds: FilterTabs.UPLOADER,
    documentPeriod: FilterTabs.DOCUMENT_PERIOD,
    uploadDate: FilterTabs.UPLOAD_DATE,
    fileExtensions: FilterTabs.DOCUMENT_TYPE,
    auditLogFilter: FilterTabs.AUDIT_LOG,
    templateCounterparty: FilterTabs.TEMPLATE_COUNTERPARTY,
    templateCustomer: FilterTabs.TEMPLATE_CUSTOMER,
    templateClass: FilterTabs.TEMPLATE_CLASS,
    templateBasis: FilterTabs.TEMPLATE_BASIS,
    templateLastEditedBy: FilterTabs.TEMPLATE_LAST_EDITED_BY,
    templateOwner: FilterTabs.TEMPLATE_OWNER,
    templateCreatedBy: FilterTabs.TEMPLATE_CREATED_BY,
    templateType: FilterTabs.TEMPLATE_TYPE,
    templateIds: FilterTabs.TEMPLATE_IDS,
    taskAssignee: FilterTabs.TASK_ASSIGNEE,
    taskCreatedBy: FilterTabs.TASK_CREATED_BY,
    taskClient: FilterTabs.TASK_CLIENT,
    taskCreatedOn: FilterTabs.TASK_CREATED_ON,
    taskCompletedBy: FilterTabs.TASK_COMPLETED_BY,
    taskCompletedOn: FilterTabs.TASK_COMPLETED_ON,
    taskClose: FilterTabs.TASK_CLOSE,
    taskPriority: FilterTabs.TASK_PRIORITY,
    taskAttachments: FilterTabs.TASK_ATTACHMENTS,
    taskGroup: FilterTabs.TASK_GROUP,
    taskBlockedBy: FilterTabs.TASK_DEPENDENCIES,
    taskBlocking: FilterTabs.TASK_DEPENDENCIES,
    taskRelated: FilterTabs.TASK_DEPENDENCIES,
    taskChat: FilterTabs.TASK_CHAT,
    taskStage: FilterTabs.TASK_STAGE,
    orgTaskTemplateType: FilterTabs.ORG_TASK_TEMPLATE_TYPE,
    orgTaskTemplateGroup: FilterTabs.ORG_TASK_TEMPLATE_GROUP,
    orgTaskTemplateAssigneeRole: FilterTabs.ORG_TASK_TEMPLATE_ASSIGNEE_ROLE,
    orgTaskTemplateCreatedBy: FilterTabs.ORG_TASK_TEMPLATE_CREATED_BY,
    orgCloseStage: FilterTabs.ORG_CLOSE_STAGE,
    closeStatuses: FilterTabs.CLOSE_STATUS,
    orgService: FilterTabs.ORG_SERVICE,
    closeTemplate: FilterTabs.CLOSE_TEMPLATE,
    applicationTypes: FilterTabs.APPLICATION_TYPE,
    progressStatuses: FilterTabs.PROGRESS,
    healthStatuses: FilterTabs.HEALTH,
    workClient: FilterTabs.WORK_CLIENT,
    workStartDate: FilterTabs.WORK_START_DATE,
    workDeliveryDate: FilterTabs.WORK_DELIVERY_DATE,
    applicationDate: FilterTabs.APPLICATION_DATE,
    coaLastUpdated: FilterTabs.COA_LAST_UPDATED,
    coaAppliedAt: FilterTabs.COA_APPLIED_AT,
    daysInStage: FilterTabs.DAYS_IN_STAGE,
    reviewedFields: FilterTabs.REVIEW,
    unreviewedFields: FilterTabs.REVIEW,
    reviewer: FilterTabs.REVIEW,
    ruleReviewStatuses: FilterTabs.RULES_REVIEW,
    ruleReviewer: FilterTabs.RULES_REVIEW,
    counterpartyReviewedFields: FilterTabs.COUNTERPARTY_REVIEW,
    counterpartyUnreviewedFields: FilterTabs.COUNTERPARTY_REVIEW,
    counterpartyReviewer: FilterTabs.COUNTERPARTY_REVIEW,
    disabledFilter: FilterTabs.COUNTERPARTY_DISABLED,
    engagementTeam: FilterTabs.ENGAGEMENT_TEAM,
    isEligible1099: FilterTabs.ELIGIBLE_1099,
    excludeCreditCardTransactions: FilterTabs.ELIGIBLE_1099,
    excludePaymentProcessorTransactions: FilterTabs.ELIGIBLE_1099,
    includeExcludedAmounts: FilterTabs.ELIGIBLE_1099,
    excludeFrom1099: FilterTabs.EXCLUDE_1099,
    accountClasses: FilterTabs.LEDGER_GROUP,
    schedulePeriod: FilterTabs.SCHEDULE_PERIOD,
    scheduleTemplate: FilterTabs.SCHEDULE_TEMPLATE,
    scheduleClient: FilterTabs.SCHEDULE_CLIENT,
    schedulesReportTemplate: FilterTabs.SCHEDULES_REPORT_TEMPLATE,
    schedulesReportGroup: FilterTabs.SCHEDULES_REPORT_GROUP,
    holdingAccount: FilterTabs.HOLDING_ACCOUNT,
    termStart: FilterTabs.TERM_START,
    termEnd: FilterTabs.TERM_END,
    invoiceTerms: FilterTabs.INVOICE_TERMS,
    invoiceSentStatus: FilterTabs.INVOICE_SENT_STATUS,
    invoiceCreatedOn: FilterTabs.INVOICE_CREATED_ON,
    invoiceDueDate: FilterTabs.INVOICE_DUE_DATE,
    invoiceDate: FilterTabs.INVOICE_DATE,
    agingPeriod: FilterTabs.AGING_PERIOD,
};

const isDefaultInvoiceStatuses = (invoiceStatuses: InvoiceStatus[]): boolean =>
    invoiceStatuses.length === defaultInvoiceStatuses.length &&
    invoiceStatuses.every((status) => defaultInvoiceStatuses.includes(status));

const isDefaultBillStatuses = (billStatuses: BillStatus[]): boolean =>
    billStatuses.length === defaultBillStatuses.length &&
    billStatuses.every((status) => defaultBillStatuses.includes(status));

const isDefaultRuleStatuses = (ruleStatuses: RuleStatus[]): boolean =>
    ruleStatuses.length === defaultRuleStatuses.length &&
    ruleStatuses.every((status) => defaultRuleStatuses.includes(status));

const isDefaultRuleSources = (ruleSources: RuleSourceFilter[]): boolean =>
    ruleSources.length === defaultRuleSources.length &&
    ruleSources.every((source) => defaultRuleSources.includes(source));
